import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import MuiIconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MuiDialogTitle from "@mui/material/DialogTitle";
import MuiTypography from "@mui/material/Typography";
import MuiTabs from "@mui/material/Tabs";
import MuiTab from "@mui/material/Tab";
import Box from "@mui/material/Box";

import Modal from "components/styled/modal/Modal";
import pxToRem from "libs/pxToRem";
import DocumentViewer from "components/business/documentViewer/DocumentViewer";
import { useTicketsVentaUrls } from "components/app/consumo/Revision/actions/getTicketVenta";

interface ITicketVentaModal {
  solicitudId: number | undefined;
  urlsTicketVenta: string[];
  open: boolean;
  onClose: () => void;
}

const TicketVentaModal = ({
  solicitudId,
  urlsTicketVenta,
  open,
  onClose,
}: ITicketVentaModal) => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) setActiveTab(0);
  }, [open]);

  const ticketUrls = urlsTicketVenta ?? [];
  const hasTickets = ticketUrls.length > 0;
  const safeActiveTab = hasTickets
    ? Math.min(activeTab, ticketUrls.length - 1)
    : 0;
  const { data: resolvedUrls, isLoading: loadingTickets } = useTicketsVentaUrls(
    ticketUrls,
    open && hasTickets,
  );

  const isLoading = hasTickets && loadingTickets[safeActiveTab];
  const currentUrl = resolvedUrls[safeActiveTab] || "";

  return (
    <Modal
      onClose={onClose}
      opened={open}
      modalHeader={
        <Box>
          <IconButton aria-label="close" onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>

          <ModalTitle>
            <Title>Ticket de venta - Solicitud {solicitudId}</Title>
          </ModalTitle>
        </Box>
      }
      maxWidth="xl"
      withPadding={false}
    >
      {ticketUrls.length > 1 && (
        <TicketTabs
          value={safeActiveTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {ticketUrls.map((_, index) => (
            <MuiTab key={index} label={`Ticket ${index + 1}`} />
          ))}
        </TicketTabs>
      )}
      <DocumentViewer
        documentUrl={currentUrl}
        loading={isLoading}
        hasDocument={hasTickets}
        emptyTitle="Ticket no disponible"
        emptyMessage="No hay un ticket de venta asociado a esta solicitud."
      />
    </Modal>
  );
};

export default TicketVentaModal;

const Title = styled(MuiTypography)({
  fontSize: pxToRem(20),
  fontWeight: "bold",
  lineHeight: 1.89,
  letterSpacing: 0.45,
  textAlign: "center",
});

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

const ModalTitle = styled(MuiDialogTitle)({
  "&.MuiDialogTitle-root": {
    marginTop: pxToRem(15),
  },
});

const TicketTabs = styled(MuiTabs)({
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "#fff",
  borderBottom: "1px solid #e0e0e0",
});
